import Application from '@Common/ApplicationCore/Application';
import { Dialog, IControl, IDialog, Input, LayoutBuilder } from '@Common/Controls';
import { DataServiceProvider } from '@Common/Core/DataServiceProvider';
import { asyncUI } from '@Common/Core/Decorators';
import { MessageBox } from '@Common/Dialogs/MessageBox';
import { InsightExceptionType } from '@Common/Enums/Enum';

const resources = strings.loginControl;

export class ChangePasswordDialog {
    private dialog: IDialog;
    private layout: IControl;

    private oldPassword: Input;
    private newPassword: Input;
    private confirmPassword: Input;

    private enforcedPolicy: boolean = !Application.current.model.user.enforcePasswordPolicy;
    private oldPasswordWrong = false;

    constructor(private dataServiceProvider: DataServiceProvider) {
        this.dialog = Dialog({
            className: 'wizard',
            title: resources.changePasswordTitle,
            width: 300,
            height: 440,
            hideCancel: false,
            closeByEnter: false
        });
        this.createLayout();
        this.dialog.ok(() => {
            this.onChangePassword();
            return true;
        });
        this.dialog.setOkButtonState(false);
    }

    public open(): void {
        this.dialog.wrap(this.layout);
        this.validate();
    }

    private createLayout(): void {
        this.oldPassword = new Input({ width: '100%' });
        this.oldPassword.getControl().attr('type', 'password');
        this.oldPassword.onChange(() => {
            this.oldPasswordWrong = false;
            this.validate();
        });

        this.newPassword = new Input({ width: '100%' });
        this.newPassword.getControl().attr('type', 'password');

        this.confirmPassword = new Input({ width: '100%' });
        this.confirmPassword.getControl().attr('type', 'password');
        this.confirmPassword.onChange(() => this.validate());

        const loginInput = new Input({ width: '100%', canEdit: false });
        loginInput.setValue(altosoft.model.user.parameters.Login);
        loginInput.setState(false);

        const b = new LayoutBuilder(false);
        b.addControl(resources.usernameTitle, loginInput);
        b.addControl(resources.passwordTitle, this.oldPassword);
        b.addControl(resources.newPasswordTitle, this.newPassword);
        b.addControl(resources.confirmPasswordTitle, this.confirmPassword);

        this.layout = b.createLayout();

        if (!this.enforcedPolicy) {
            this.newPassword.getControl().pStrength({
                changeBackground: false,
                onPasswordStrengthChanged: () => {
                    this.enforcedPolicy = false;
                    this.validate();
                },
                onValidatePassword: () => {
                    this.enforcedPolicy = true;
                    this.validate();
                }
            });
        } else {
            this.newPassword.onChange(() => this.validate());
        }
    }

    private validate(): void {
        let valid = true;
        if (this.oldPassword.getValue() === '') {
            this.oldPassword.setError(resources.passwordIsEmpty);
            valid = false;
        } else if (this.oldPasswordWrong) {
            this.oldPassword.setError(resources.wrongPassword);
            valid = false;
        } else {
            this.oldPassword.removeError();
        }

        if (this.newPassword.getValue() === '') {
            this.newPassword.setError(resources.newPasswordIsEmpty);
            valid = false;
        } else if (!this.enforcedPolicy) {
            this.newPassword.setError(resources.pStrength);
            valid = false;
        } else if (this.newPassword.getValue() === this.oldPassword.getValue()) {
            this.newPassword.setError(resources.passwordMustDiffer);
            valid = false;
        } else {
            this.newPassword.removeError();
        }

        if (this.newPassword.getValue() !== this.confirmPassword.getValue()) {
            this.confirmPassword.setError(resources.confirmMustMatch);
            valid = false;
        } else {
            this.confirmPassword.removeError();
        }

        this.dialog.setOkButtonState(valid);
    }

    @asyncUI('dialog', { setting: { loadingTimeout: 0, message: resources.changingPassword } })
    public async onChangePassword(): Promise<void> {
        try {
            await this.dataServiceProvider.changePassword(this.oldPassword.getValue(), this.newPassword.getValue());
            this.dialog.close();
            const { passwordChanged: text, changePasswordTitle: dialogTitle } = resources;
            const messageBox = new MessageBox({ text, dialogTitle });
            messageBox.show();
        } catch (errorType) {
            this.oldPasswordWrong = errorType === InsightExceptionType.wrongPassword;
            this.validate();
        }
    }
}
